import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Trophy, Target, ChartBar, Medal, ArrowsLeftRight, UserCircle } from "@phosphor-icons/react";

export default function HeadToHead() {
  const [users, setUsers] = useState([]);
  const [u1, setU1] = useState("");
  const [u2, setU2] = useState("");
  const [h2h, setH2h] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers((data || []).filter((u) => (u.role === "jugador" || u.role === "profesor") && u.is_active !== false))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!u1 || !u2 || u1 === u2) { setH2h(null); return; }
    setLoading(true);
    api.get(`/statistics/head-to-head?user1=${u1}&user2=${u2}`).then(({ data }) => setH2h(data)).catch(() => setH2h(null)).finally(() => setLoading(false));
  }, [u1, u2]);

  const renderCard = (p, side) => p && (
    <div className="card-tactical p-6" data-testid={`h2h-player-${side}`}>
      <p className={`text-xs tracking-[0.2em] uppercase font-semibold mb-1 ${side === 1 ? "text-[#CCFF00]" : "text-orange-400"}`}>Jugador {side}</p>
      <h3 className="font-['Barlow_Condensed'] text-3xl font-black uppercase tracking-tighter">{p.name}</h3>
      <div className="flex gap-2 mt-2 flex-wrap">
        {p.category && <Badge variant="outline" className="bg-white/5 text-gray-400 border-white/10 text-[10px] uppercase">Cat. {p.category}</Badge>}
        {p.state && p.country && <Badge variant="outline" className="bg-white/5 text-gray-400 border-white/10 text-[10px] uppercase">{p.state}, {p.country}</Badge>}
      </div>
      <div className="grid grid-cols-2 gap-3 mt-5 pt-5 border-t border-white/5">
        <div><p className="font-['Barlow_Condensed'] text-3xl font-black text-white">#{p.ranking}</p><p className="text-[10px] uppercase tracking-widest text-gray-500">Ranking</p></div>
        <div><p className="font-['Barlow_Condensed'] text-3xl font-black text-[#CCFF00]">{p.points}</p><p className="text-[10px] uppercase tracking-widest text-gray-500">Puntos</p></div>
        <div><p className="font-['Barlow_Condensed'] text-3xl font-black text-white">{p.games_won}/{p.games_played}</p><p className="text-[10px] uppercase tracking-widest text-gray-500">Victorias</p></div>
        <div><p className="font-['Barlow_Condensed'] text-3xl font-black text-white">{p.win_rate}%</p><p className="text-[10px] uppercase tracking-widest text-gray-500">Win Rate</p></div>
      </div>
      <Link to={`/dashboard/jugador/${p.id}`} className="inline-flex items-center gap-1 text-xs text-[#CCFF00] hover:underline uppercase tracking-wider mt-4" data-testid={`h2h-profile-link-${side}`}>
        <UserCircle size={14} /> Ver perfil completo
      </Link>
    </div>
  );

  const u1Name = users.find((u) => u.id === u1)?.name;
  const u2Name = users.find((u) => u.id === u2)?.name;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="h2h-page">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2"><ArrowsLeftRight size={14} />Comparador</p>
        <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Head to Head</h1>
        <p className="text-sm text-gray-400 mt-1">Compara dos jugadores lado a lado y ve su historial de enfrentamientos</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-semibold mb-2">Jugador 1</p>
          <Select value={u1} onValueChange={setU1}>
            <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="h2h-select-1"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-[300px]">
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.category ? ` · Cat. ${u.category}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-orange-400 font-semibold mb-2">Jugador 2</p>
          <Select value={u2} onValueChange={setU2}>
            <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="h2h-select-2"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-[300px]">
              {users.filter((u) => u.id !== u1).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.category ? ` · Cat. ${u.category}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && <div className="flex items-center justify-center h-32"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>}

      {h2h && !loading && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-4 items-start">
            {renderCard(h2h.player1, 1)}
            <div className="card-tactical p-6 text-center min-w-[180px]" data-testid="h2h-vs-block">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Enfrentamientos</p>
              <p className="font-['Barlow_Condensed'] text-5xl font-black text-white">{h2h.versus_count}</p>
              <div className="flex items-center justify-center gap-3 mt-3 text-lg font-['Barlow_Condensed'] font-black">
                <span className="text-[#CCFF00]" data-testid="h2h-u1-wins">{h2h.u1_wins_vs_u2}</span>
                <span className="text-gray-600">-</span>
                <span className="text-orange-400" data-testid="h2h-u2-wins">{h2h.u2_wins_vs_u1}</span>
              </div>
              <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-3 pt-3 border-t border-white/5">Juntos</p>
              <p className="font-['Barlow_Condensed'] text-2xl font-black text-white">{h2h.together_count}</p>
            </div>
            {renderCard(h2h.player2, 2)}
          </div>

          {h2h.versus_matches?.length > 0 && (
            <div className="card-tactical p-6" data-testid="h2h-versus-list">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2"><Target size={14} />Historial</p>
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight mb-4">Enfrentamientos directos</h2>
              <div className="space-y-2">
                {h2h.versus_matches.map((m) => {
                  const s1 = m.sets.filter((s) => s.winner === 1).length;
                  const s2 = m.sets.filter((s) => s.winner === 2).length;
                  const setScores = m.sets.map((s) => `${s.pair1_games}-${s.pair2_games}`).join(" · ");
                  const u1Won = (m.u1_side === 1 && m.winner_pair === 1) || (m.u1_side === 2 && m.winner_pair === 2);
                  return (
                    <Link to={`/dashboard/marcador/${m.id}`} key={m.id} className="block bg-[#0A0A0A] border border-white/10 p-3 hover:border-[#CCFF00]/40 transition-colors" data-testid={`h2h-match-${m.id}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-['JetBrains_Mono']">{m.date}{m.time ? ` · ${m.time}` : ""}</span>
                            {m.category && <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[9px]">Cat. {m.category}</Badge>}
                            <Badge variant="outline" className={`text-[9px] ${u1Won ? "bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30" : "bg-orange-500/10 text-orange-400 border-orange-500/30"}`}>Gano {u1Won ? u1Name : u2Name}</Badge>
                          </div>
                          <p className="font-['JetBrains_Mono'] text-xs text-gray-400">{setScores}</p>
                        </div>
                        <span className="font-['Barlow_Condensed'] font-black text-2xl">
                          <span className={m.winner_pair === 1 ? "text-[#CCFF00]" : "text-gray-500"}>{s1}</span>
                          <span className="text-gray-600 mx-0.5">-</span>
                          <span className={m.winner_pair === 2 ? "text-[#CCFF00]" : "text-gray-500"}>{s2}</span>
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {h2h.together_matches?.length > 0 && (
            <div className="card-tactical p-6" data-testid="h2h-together-list">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2"><Medal size={14} />Juntos</p>
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight mb-4">Partidos como companeros</h2>
              <div className="space-y-2">
                {h2h.together_matches.map((m) => {
                  const won = m.winner_pair === m.side;
                  const setScores = m.sets.map((s) => `${s.pair1_games}-${s.pair2_games}`).join(" · ");
                  return (
                    <Link to={`/dashboard/marcador/${m.id}`} key={m.id} className="block bg-[#0A0A0A] border border-white/10 p-3 hover:border-[#CCFF00]/40 transition-colors">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <span className="text-[10px] uppercase tracking-widest text-gray-500 font-['JetBrains_Mono']">{m.date}{m.time ? ` · ${m.time}` : ""}</span>
                          <p className="font-['JetBrains_Mono'] text-xs text-gray-400 mt-0.5">{setScores}</p>
                        </div>
                        <Badge variant="outline" className={`text-[10px] ${won ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}`}>{won ? "Victoria" : "Derrota"}</Badge>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {!h2h && (!u1 || !u2) && (
        <div className="card-tactical p-12 text-center">
          <Trophy size={48} weight="duotone" className="text-[#CCFF00] mx-auto mb-4" />
          <p className="text-gray-400">Selecciona dos jugadores para comparar</p>
        </div>
      )}
    </div>
  );
}
